import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  DataTable,
  EmptyState,
  InlineAlert,
  Loader,
  LoadingButton,
} from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { rosterSourceSummary } from "src/grading/utils/rosterSource";
import { ApiError } from "src/models";
import { useAppDispatch } from "src/store/hooks";
import {
  useCreateDistrictMutation,
  useListDistrictsQuery,
} from "src/store/reducers/districts/districtsApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";

const LICENSE_COLOR = {
  trial: "default",
  active: "success",
  suspended: "error",
} as const;

/** Platform administrators see every district; district staff land on their own. */
const DistrictsPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { hasPermissions } = usePermissions();
  const { data: districts, isLoading, isError } = useListDistrictsQuery();
  const [createDistrict, { isLoading: creating }] = useCreateDistrictMutation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("America/Chicago");
  const [domains, setDomains] = useState("");
  const [error, setError] = useState<string | null>(null);
  const canCreate = hasPermissions(
    permissions.CREATE_DISTRICTS.action,
    permissions.CREATE_DISTRICTS.subject,
  );

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!canCreate && districts && districts.length === 1)
      history.replace(`/grading/districts/${districts[0].id}`);
  }, [canCreate, districts, history]);

  const submit = async () => {
    setError(null);
    try {
      const district = await createDistrict({
        name: name.trim(),
        timezone: timezone.trim() || undefined,
        ssoDomains: domains
          .split(/[,\s]+/)
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
      }).unwrap();
      setOpen(false);
      setName("");
      setDomains("");
      history.push(`/grading/districts/${district.id}`);
    } catch (e) {
      const err = e as ApiError;
      setError(
        Array.isArray(err.data?.message)
          ? err.data.message.join(", ")
          : (err.data?.message ??
              t("districts.createError", {
                defaultValue: "Could not create the district",
              })),
      );
    }
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <PageHeader
        title={t("districts.title", { defaultValue: "Districts" })}
        actions={
          canCreate ? (
            <Button variant="contained" onClick={() => setOpen(true)}>
              {t("districts.new", { defaultValue: "New district" })}
            </Button>
          ) : undefined
        }
      />
      {isError && (
        <InlineAlert severity="error">
          {t("districts.loadError", {
            defaultValue:
              "Could not load districts. Check your connection and refresh.",
          })}
        </InlineAlert>
      )}
      <Paper sx={{ p: 2, mt: isError ? 2 : 0 }}>
        <DataTable
          aria-label={t("districts.title", { defaultValue: "Districts" })}
          dense
          rows={districts ?? []}
          getRowKey={(d) => d.id}
          onRowClick={(d) => history.push(`/grading/districts/${d.id}`)}
          emptyState={
            <EmptyState
              title={t("districts.empty", {
                defaultValue: "No districts yet.",
              })}
            />
          }
          columns={[
            {
              key: "name",
              header: t("districts.colDistrict", { defaultValue: "District" }),
              render: (d) => d.name,
            },
            {
              key: "license",
              header: t("districts.colLicense", { defaultValue: "License" }),
              render: (d) => (
                <Chip
                  size="small"
                  color={LICENSE_COLOR[d.licenseStatus]}
                  label={t(`district.licenseStatus.${d.licenseStatus}`, {
                    defaultValue: d.licenseStatus,
                  })}
                />
              ),
            },
            {
              key: "sso",
              header: t("districts.colSso", { defaultValue: "SSO domains" }),
              render: (d) => d.ssoDomains.join(", ") || "—",
            },
            {
              key: "roster",
              header: t("districts.colRoster", {
                defaultValue: "Roster source",
              }),
              // FR-035: vendor, or "CSV upload" / "Roster sheet" with the last import date.
              render: (d) =>
                rosterSourceSummary(t, d.rosterSource, i18n.language),
            },
            {
              key: "timezone",
              header: t("districts.colTimezone", { defaultValue: "Time zone" }),
              render: (d) => d.timezone ?? "—",
            },
          ]}
        />
      </Paper>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="new-district-title"
      >
        <DialogTitle id="new-district-title">
          {t("districts.new", { defaultValue: "New district" })}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label={t("districts.name", { defaultValue: "District name" })}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label={t("districts.timezoneIana", {
              defaultValue: "Time zone (IANA)",
            })}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            helperText={t("districts.timezoneHelper", {
              defaultValue:
                "Join codes expire at the end of the session day in this zone",
            })}
          />
          <TextField
            label={t("districts.emailDomains", {
              defaultValue: "Staff email domains",
            })}
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            helperText={t("districts.domainsHelper", {
              defaultValue:
                "Comma-separated, e.g. demo-usd.org. Staff with these domains sign in through the district identity provider.",
            })}
          />
          {error && <InlineAlert severity="error">{error}</InlineAlert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <LoadingButton
            variant="contained"
            loading={creating}
            disabled={name.trim().length < 2}
            onClick={submit}
            label={t("common.create", { defaultValue: "Create" })}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DistrictsPage;
